import { sb } from './client';
import type {
  AppGroup,
  AppUser,
  Role,
  UserGroupMembership,
} from './types';

/** Fetch authenticated user preferences from database. Returns null if not found or unauthenticated. */
export async function getUserPreferences(): Promise<Record<string, unknown> | null> {
  const c = sb();
  if (!c) return null;
  const { data, error } = await c.from('user_preferences').select('config').maybeSingle();
  if (error) {
    console.error('Failed to load user preferences:', error);
    return null;
  }
  return (data?.config as Record<string, unknown>) ?? null;
}

/** Save authenticated user preferences to database. */
export async function saveUserPreferences(
  userId: string,
  config: Record<string, unknown>,
): Promise<void> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { error } = await c.from('user_preferences').upsert(
    {
      user_id: userId,
      config,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' },
  );
  if (error) throw error;
}

/** Fetch all defined application groups. */
export async function getAppGroups(): Promise<AppGroup[]> {
  const c = sb();
  if (!c) return [];
  const { data, error } = await c.from('app_groups').select('*');
  if (error) throw error;
  return (data ?? []) as AppGroup[];
}

/** Fetch group memberships for a user (or current user). */
export async function getUserGroups(userId?: string): Promise<UserGroupMembership[]> {
  const c = sb();
  if (!c) return [];
  let q = c.from('user_group_memberships').select('*');
  if (userId) {
    q = q.eq('user_id', userId);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as UserGroupMembership[];
}

/** Check if a user (or current user) has the admin group role. */
export async function isUserAdmin(userId?: string): Promise<boolean> {
  const c = sb();
  if (!c) return false;
  const { data, error } = await c.rpc('is_admin', userId ? { p_user_id: userId } : {});
  if (error) {
    console.error('Failed to check admin status:', error);
    return false;
  }
  return !!data;
}

// ── Role management (docs/role-management-plan.md §3.1) ──────────

export class ForbiddenError extends Error {
  constructor(message = 'forbidden') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

function classifyRpcError(error: { code?: string; message: string }): Error {
  if (error.code === '42501') return new ForbiddenError(error.message);
  const err = new Error(error.message);
  // PostgREST surfaces pg codes on the error object; carry them through
  // so callers can distinguish P0001 (last-admin) from anything else.
  if (error.code) (err as Error & { code?: string }).code = error.code;
  return err;
}

/**
 * Directory of app users. Admins get everyone; commercials get member +
 * commercial rows only (admins withheld server-side); members get 42501.
 */
export async function listAppUsers(): Promise<AppUser[]> {
  const c = sb();
  if (!c) throw new ForbiddenError('Not connected');
  const { data, error } = await c.rpc('list_app_users');
  if (error) throw classifyRpcError(error);
  return ((data ?? []) as Array<Record<string, unknown>>).map((u) => ({
    user_id: String(u.user_id),
    email: u.email == null ? '' : String(u.email),
    role: (u.role as AppUser['role']) ?? 'member',
    created_at: String(u.created_at),
    last_sign_in_at: u.last_sign_in_at == null ? null : String(u.last_sign_in_at),
  }));
}

/** Assign a role. Admin-only server-side; propagates P0001 verbatim for the panel. */
export async function setUserRole(userId: string, role: Role): Promise<Role> {
  const c = sb();
  if (!c) throw new ForbiddenError('Not connected');
  const { data, error } = await c.rpc('set_user_role', { p_user_id: userId, p_role: role });
  if (error) throw classifyRpcError(error);
  return (data as Role) ?? role;
}

/**
 * Resolve a user's DB role. Self by default; other uuids need admin
 * server-side. Fails closed to 'member' on any error.
 */
export async function getUserRole(userId?: string): Promise<Role> {
  const c = sb();
  if (!c) return 'member';
  const { data, error } = await c.rpc('user_role', userId ? { p_user_id: userId } : {});
  if (!error && typeof data === 'string') return data as Role;
  if (error?.code === 'PGRST202') {
    // Backend predates role management (migration 20260825 not applied yet):
    // degrade to the legacy is_admin probe instead of failing everyone closed
    // to member during the deploy window between frontend and migration.
    return (await isUserAdmin(userId)) ? 'admin' : 'member';
  }
  console.error('Failed to resolve user role:', error);
  return 'member';
}
